import React, { useState } from 'react';
import { Variants } from 'framer-motion';
import NextLink from 'next/link';
import { useRouter } from 'next/router';

import { Dropdown, Grouping } from '@definitions/header.types';
import { LinkSetType } from '@definitions/common.types';
import { ROUTE } from '@utils/constants';
import { renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';

import { Container, Trigger, List, Item, Link } from './headerDropdown.styles';

interface iHeaderDropdown {
  items: Dropdown[];
  groupings: Grouping[];
  onMouseOver: () => void;
  onMouseOut: () => void;
}

const itemVariants: Variants = {
  open: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 200, damping: 25 },
  },
  closed: { opacity: 0, y: -20, transition: { duration: 0.3 } },
};

export const HeaderDropdown = ({ items, groupings, onMouseOver, onMouseOut }: iHeaderDropdown) => {
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();

  const renderTrigger = () => (
    <Trigger
      isActive={router.pathname.includes(ROUTE.ARTWORKS)}
      isOpen={isOpen}
      onMouseOver={() => {
        setIsOpen(true);
        onMouseOver();
      }}
    >
      Artworks
    </Trigger>
  );

  const renderArtworksLink = renderWhenTrueOtherwise(
    () =>
      groupings?.map(({ slug }, index) => {
        return (
          index === 0 && (
            <NextLink
              key={index}
              href={`${ROUTE.ARTWORKS}${ROUTE.GROUPINGS}/[grouping]`}
              as={`${ROUTE.ARTWORKS}${ROUTE.GROUPINGS}/${slug}`}
              passHref
            >
              {renderTrigger()}
            </NextLink>
          )
        );
      }),
    () => (
      <NextLink href={`${ROUTE.ARTWORKS}`} as={`${ROUTE.ARTWORKS}`} passHref>
        {renderTrigger()}
      </NextLink>
    )
  );

  const renderList = renderWhenTrue(() => (
    <List
      initial={false}
      style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
      animate={isOpen ? 'open' : 'closed'}
      variants={{
        open: {
          transition: {
            type: 'spring',
            bounce: 0,
            duration: 0.3,
            delayChildren: 0.2,
            staggerChildren: 0.25,
          },
        },
        closed: {
          transition: {
            type: 'spring',
            bounce: 0,
            duration: 0.3,
          },
        },
      }}
    >
      {items.map(({ link: { slug, displayText, type, urlPath } }, index) => (
        <Item variants={itemVariants} key={index}>
          <NextLink href={type === LinkSetType.INTERNAL ? `/${slug}` : urlPath} passHref>
            <Link>{displayText}</Link>
          </NextLink>
        </Item>
      ))}
    </List>
  ));

  return (
    <Container
      onMouseLeave={() => {
        setIsOpen(false);
        onMouseOut();
      }}
    >
      {renderArtworksLink(groupings.length > 0)}
      {renderList(!router.pathname?.includes(ROUTE.ARTWORKS) && router.pathname !== ROUTE.TRADE)}
    </Container>
  );
};
